import React from "react";
import {DashboardOrder} from "../../../api/ApiTypes";
import {formatDateNumeric} from "../../../utils/dateUtils";
import {AIBadge} from "../../../components/AIBadge.tsx";
import {MessageCircle} from "lucide-react";
import {StatusBadge} from "../../../components/StatusBadge.tsx";
import {dashboardColumns} from "./dashboardTableConfig.ts";
import Tooltip from "../../../components/Tooltip.tsx";
interface DashboardTableViewProps {
    data: DashboardOrder[];
    onSort: (field: string) => void;
    sorting: { field: string; direction: string };
    onView: (id: number) => void;
    getSortIcon: (field: string) => React.ReactNode;
    getDueDateClass: (dueDate: string | null) => string;
}

export const DashboardTableView: React.FC<DashboardTableViewProps> = ({
                                                                          data,
                                                                          onSort,
                                                                          onView,
                                                                          getSortIcon,
                                                                          getDueDateClass,
                                                                      }) => {
    return (
        <div className="hidden md:block border border-gray-100 rounded-lg shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
                <table className="w-full table-fixed">
                    <thead className="bg-gray-100">
                    <tr>
                        {dashboardColumns.map((col, idx) => (
                            <th
                                key={idx}
                                className={`px-1 py-2 text-xs font-semibold text-gray-600 tracking-wider border-b border-gray-100 hover:text-blue-600 transition-colors hover:cursor-pointer ${
                                    col.align === "center" ? "text-center" : "text-left"
                                } ${col.showOn ? `hidden ${col.showOn}:table-cell` : ""}`}
                                style={{ width: col.width }}
                                onClick={() => onSort(col.field)}
                            >
                                {col.label}
                                {getSortIcon(col.field)}
                            </th>
                        ))}
                    </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-100">
                    {data.map((order) => (
                        <tr key={order.id} className="hover:bg-gray-50/50 hover:cursor-pointer" onClick={() => onView(order.id)}>
                            <td className="px-1 py-1.5 text-sm text-center">
                                <Tooltip text={order.usaFileNumber}>
                                    <div className="truncate">
                                        <div className="font-medium text-gray-900 truncate text-center relative">
                                            {order.usaFileNumber}
                                            {/* Message indicator bubble with high z-index */}
                                            {order.appraiserUnreadMessages && (
                                                <div
                                                    className="absolute z-10 flex items-center justify-center w-4 h-4 bg-red-500 rounded-full text-white text-xs font-bold"
                                                    style={{ top: "0px", right: "0px" }}
                                                >
                                                    <MessageCircle size={10} className="text-white" />
                                                </div>
                                            )}
                                        </div>
                                        <div className="text-gray-500 flex items-center justify-center mt-0.5">
                                            <AIBadge
                                                attentionIndicator={order.attentionIndicator}
                                                size={"small"}
                                            />
                                        </div>
                                    </div>
                                </Tooltip>
                            </td>
                            <td className="px-1 py-1.5 text-sm text-center">
                                <StatusBadge
                                    statusCode={order.orderStatus}
                                    statusId={order.statusId}
                                />
                            </td>
                            <td className="px-1 py-1.5 text-sm text-center hidden lg:table-cell">
                                <Tooltip text={formatDateNumeric(order.assignmentActualDate)}>
                                    <span>{formatDateNumeric(order.assignmentActualDate)}</span>
                                </Tooltip>
                            </td>
                            <td className="px-1 py-1.5 text-sm text-center hidden lg:table-cell">
                                <Tooltip text={formatDateNumeric(order.inspectionActualDate)}>
                                    <span>{formatDateNumeric(order.inspectionActualDate)}</span>
                                </Tooltip>
                            </td>
                            <td className="px-1 py-1.5 text-sm text-center">
                                <Tooltip text={formatDateNumeric(order.appraiserDueDate)}>
                                    <span className={`px-1.5 py-0.5 rounded-full ${getDueDateClass(order.appraiserDueDate)}`}>
                                    {formatDateNumeric(order.appraiserDueDate)}
                                </span>
                                </Tooltip>
                            </td>
                            <td className="px-1 py-1.5 text-sm text-left">
                                <Tooltip
                                    text={`${order.propertyAddressLine1} ${order.propertyCity}, ${order.propertyStateCode} ${order.propertyZip}`}
                                >
                                <div className="truncate">
                                    <div className="font-medium text-gray-900 truncate">
                                        {order.propertyAddressLine1}
                                    </div>
                                    <div className="text-gray-500 truncate">
                                        {order.propertyCity}, {order.propertyStateCode} {order.propertyZip}
                                    </div>
                                </div>
                                </Tooltip>
                            </td>
                            <td className="px-1 py-1.5 text-sm text-left">
                                <Tooltip text={order.borrowerName}>
                                    <div className="truncate">{order.borrowerName}</div>
                                </Tooltip>
                            </td>
                            <td className="px-1 py-1.5 text-sm text-left">
                                <Tooltip text={order.appraiserName}>
                                    <div className="truncate">{order.appraiserName}</div>
                                </Tooltip>
                            </td>
                        </tr>
                    ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}